import { Lock } from "lucide-react";

export type FactTone = "good" | "warn" | "bad";
export interface Fact { label: string; value: string; sub?: string | null; tone?: FactTone; gated?: boolean }

const TONE: Record<FactTone, string> = {
  good: "text-emerald-700 dark:text-emerald-400",
  warn: "text-amber-700 dark:text-amber-400",
  bad: "text-destructive",
};

/**
 * One bordered row of plain facts: where things stand, from data the CRM already has.
 *
 * Each cell draws its own right and bottom divider as a 1px shadow, and the
 * strip's overflow clips the ones on the outer edge. A strip painted in the
 * border color with 1px gaps would also draw dividers, but it paints any empty
 * cells in a part-filled last row as a solid gray block.
 */
export function FactsStrip({ facts }: { facts: Fact[] }) {
  if (!facts.length) return null;
  return (
    <section
      aria-label="Where things stand"
      className="grid grid-cols-2 overflow-hidden rounded-lg border bg-card sm:grid-cols-[repeat(auto-fit,minmax(140px,1fr))]"
    >
      {facts.map((f) => (
        <div key={f.label} className="min-w-0 px-3 py-2 shadow-[1px_1px_0_0_var(--color-border)]">
          <div className="flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
            {f.label}
            {f.gated && <Lock className="h-2.5 w-2.5" aria-label="Only people with Financials access see this" />}
          </div>
          <div className={`text-[14.5px] font-semibold leading-snug ${f.tone ? TONE[f.tone] : ""}`}>{f.value}</div>
          {f.sub && <div className="text-xs text-muted-foreground [overflow-wrap:anywhere]">{f.sub}</div>}
        </div>
      ))}
    </section>
  );
}
